/**
 * Feature-gate paywall catalog.
 * Used by FeaturePaywallModal when a premium action is blocked (e.g. logo BG Remover).
 */

export const PAYWALL_FEATURES = {
  logo_bg_remove: {
    id: "logo_bg_remove",
    featureLabel: "BG Remover",
    eyebrow: "Upgrade to use BG Remover",
    headline: "Unlock Background Remover",
    subhead:
      "Clean logo cutouts for watermarks on images, PDFs, and Word docs — in one click.",
    recommendedPlanId: "pro",
    benefits: [
      "One-click background removal for logo watermarks",
      "Works in Watermark Maker, PDF Watermarker & Word Watermarker",
      "Export clean marks without leftover backdrop",
      "Pair with tint, crop, flip & tile layouts",
      "Commercial-ready exports for client work",
    ],
    /** Right panel — short caption only (visual does the selling). */
    storyCaption: "Transparent logos on every export",
    /** photo | pdf | word — fallback single visual */
    storyVisual: "photo",
    /** Auto-scroll cards; optional durationMs = progress-pill length (default 6000). */
    storyCards: [
      {
        id: "before_after",
        visual: "photo",
        caption: "Before → after cutout",
        chip: "BG Remover",
        durationMs: 2000,
      },
      {
        id: "on_pdf",
        visual: "pdf",
        caption: "Clean marks on every PDF page",
        chip: "PDF ready",
        durationMs: 6000,
      },
      {
        id: "on_docs",
        visual: "word",
        caption: "Sharp logos on Word exports",
        chip: "Documents",
        durationMs: 6000,
      },
    ],
    byTool: {
      watermark_maker: {
        subhead:
          "Clean logo cutouts in one click — then tint, crop, and tile on your photos.",
        benefits: [
          "One-click logo background removal",
          "Keep text, signature & logo workflows",
          "Grid & diamond tiling",
          "Export without leftover backdrop",
          "Commercial-ready marks",
        ],
        storyCaption: "Clean logos on every photo",
        storyVisual: "photo",
        storyCards: [
          {
            id: "before_after",
            visual: "photo",
            caption: "Remove the backdrop in one click",
            chip: "BG Remover",
          },
          {
            id: "on_photo",
            visual: "photo_placed",
            caption: "Place a sharp mark on product shots",
            chip: "Watermark Maker",
          },
          {
            id: "tiled",
            visual: "tile",
            caption: "Tile clean logos across the canvas",
            chip: "Grid & diamond",
          },
        ],
      },
      pdf_watermarker: {
        subhead:
          "Remove logo backgrounds, then stamp every PDF page with a clean mark.",
        benefits: [
          "Logo cutouts for multi-page PDFs",
          "Live page preview",
          "Text, signature & logo on documents",
          "Bake watermarks into download",
          "Client-ready PDFs",
        ],
        storyCaption: "Transparent logos on every page",
        storyVisual: "pdf",
        storyCards: [
          {
            id: "before_after",
            visual: "photo",
            caption: "Cut out the logo first",
            chip: "BG Remover",
          },
          {
            id: "on_pdf",
            visual: "pdf",
            caption: "Stamp every page cleanly",
            chip: "PDF Watermarker",
          },
          {
            id: "multi",
            visual: "pdf_stack",
            caption: "Same mark across the whole file",
            chip: "Multi-page",
          },
        ],
      },
      sign_pdf: {
        subhead:
          "Clean up a signature image, then place it on every PDF page you need.",
        benefits: [
          "Transparent signature uploads",
          "Live page preview",
          "Type or upload your mark",
          "Bake signatures into download",
          "Client-ready signed PDFs",
        ],
        storyCaption: "Signatures that look inked-in",
        storyVisual: "pdf",
        storyCards: [
          {
            id: "before_after",
            visual: "photo",
            caption: "Cut out the signature first",
            chip: "BG Remover",
          },
          {
            id: "on_pdf",
            visual: "pdf",
            caption: "Place it where you sign",
            chip: "Sign PDF",
          },
          {
            id: "multi",
            visual: "pdf_stack",
            caption: "Apply across pages",
            chip: "Multi-page",
          },
        ],
      },
      word_watermarker: {
        subhead:
          "Clean logo cutouts that survive Word → PDF and look sharp on letters.",
        benefits: [
          "Transparent logos on DOC/DOCX",
          "Preview after Word → PDF",
          "Match brand marks to letterheads",
          "Export watermarked PDF",
          "Consistent with image & PDF tools",
        ],
        storyCaption: "Logos that fit documents",
        storyVisual: "word",
        storyCards: [
          {
            id: "before_after",
            visual: "photo",
            caption: "Start with a transparent logo",
            chip: "BG Remover",
            durationMs: 1000,
          },
          {
            id: "on_word",
            visual: "word",
            caption: "Drop it onto letters & proposals",
            chip: "Word",
            durationMs: 4000,
          },
          {
            id: "export",
            visual: "pdf",
            caption: "Export a polished PDF",
            chip: "Ready to send",
            durationMs: 5000,
          },
        ],
      },
      editor_watermark: {
        subhead:
          "Upgrade for one-click logo background removal without leaving the studio.",
        benefits: [
          "BG Remover on logo marks",
          "Full image toolbar",
          "Stay in the editor flow",
          "Export-ready cutouts",
        ],
        storyCaption: "Studio-ready cutouts",
        storyVisual: "photo",
        storyCards: [
          {
            id: "before_after",
            visual: "photo",
            caption: "Cut out without leaving the editor",
            chip: "BG Remover",
          },
          {
            id: "on_photo",
            visual: "photo_placed",
            caption: "Preview on the live canvas",
            chip: "Studio",
          },
          {
            id: "tiled",
            visual: "tile",
            caption: "Keep editing size, tint & tile",
            chip: "Full toolbar",
          },
        ],
      },
    },
  },
  sign_pdf_per_page: {
    id: "sign_pdf_per_page",
    featureLabel: "Per-page signing",
    eyebrow: "Upgrade for custom layouts",
    headline: "Unlock different fields per page",
    subhead:
      "Build a unique signing layout on each page — signature, date, text, and checkmarks where you need them.",
    recommendedPlanId: "pro",
    benefits: [
      "Different fields and positions on every page",
      "Reuse your saved signature across customized pages",
      "Ideal for multi-section contracts and packets",
      "Same Sign PDF editor — just more control",
      "Download only the pages you customized",
    ],
    storyCaption: "One doc, page-by-page control",
    storyVisual: "pdf",
    storyCards: [
      {
        id: "on_pdf",
        visual: "pdf",
        caption: "Place fields where each page needs them",
        chip: "Per page",
      },
      {
        id: "multi",
        visual: "pdf_stack",
        caption: "Different layouts across the packet",
        chip: "Multi-page",
      },
      {
        id: "before_after",
        visual: "pdf",
        caption: "Bake a client-ready signed PDF",
        chip: "Sign PDF",
      },
    ],
    byTool: {
      sign_pdf: {
        subhead:
          "Customize each page’s signature block — free plans keep Same on all pages and This page only.",
        storyCaption: "Per-page signing layouts",
        storyVisual: "pdf",
      },
    },
  },
  sign_pdf_block_library: {
    id: "sign_pdf_block_library",
    featureLabel: "Signature block library",
    eyebrow: "Upgrade for pro layouts",
    headline: "Unlock the signature block library",
    subhead:
      "Drop domain-ready signing stacks — contracts, offers, and more — in Compact, Standard, or Spacious sizes.",
    recommendedPlanId: "pro",
    benefits: [
      "HR, Sales, Education, and Legal signing packs",
      "Three sizes per block: Compact, Standard, Spacious",
      "Drag onto the page or place with one click",
      "Ungroup anytime to customize fields",
      "Free Classic, Left align, Signature + date, and more",
    ],
    storyCaption: "Pro signing packs for every document",
    storyVisual: "pdf",
    storyCards: [
      {
        id: "catalog",
        visual: "pdf",
        caption: "Browse blocks by category",
        chip: "Library",
      },
      {
        id: "sizes",
        visual: "pdf",
        caption: "Pick Compact, Standard, or Spacious",
        chip: "3 sizes",
      },
      {
        id: "place",
        visual: "pdf",
        caption: "Place, move, and resize as one unit",
        chip: "Grouped",
      },
    ],
    byTool: {
      sign_pdf: {
        subhead:
          "Free plans include Classic. Upgrade for the full Pro signature block library.",
        storyCaption: "Signature block library",
        storyVisual: "pdf",
      },
    },
  },
  remove_soft_watermark: {
    id: "remove_soft_watermark",
    featureLabel: "Watermark-free download",
    eyebrow: "Upgrade to remove watermark",
    headline: "Download without LazyStudio.ai",
    subhead:
      "Free 1K exports include a light LazyStudio watermark. Upgrade for clean 2K and 4K downloads.",
    recommendedPlanId: "pro",
    benefits: [
      "Remove the LazyStudio.ai watermark from exports",
      "Clean 2K & 4K downloads",
      "AI upscale when you need higher resolution",
      "Commercial-ready images for listings & ads",
      "Credits that work across editing & AI tools",
    ],
    storyCaption: "Clean exports, no brand mark",
    storyVisual: "photo",
    storyCards: [
      {
        id: "with_mark",
        visual: "photo",
        caption: "Free 1K includes a soft watermark",
        chip: "Free",
        durationMs: 2800,
      },
      {
        id: "clean",
        visual: "photo_placed",
        caption: "Upgrade for watermark-free 2K & 4K",
        chip: "Pro",
        durationMs: 5000,
      },
    ],
  },
};

export const PAYWALL_TOOL_LABELS = {
  watermark_maker: "Watermark Maker",
  pdf_watermarker: "PDF Watermarker",
  word_watermarker: "Word Watermarker",
  editor_watermark: "Watermark",
  pdf_page_numbers: "PDF Page Numbers",
  sign_pdf: "Sign PDF",
  editing: "Editing Tools",
  crop: "Crop",
  circle_crop: "Circle Crop",
  padding: "Padding",
  resize: "Resize",
  shadow: "Shadow",
  outline: "Outline",
  background_color: "Background color",
  white_background: "White background",
  black_background: "Black background",
  blur: "Blur",
  gaussian_blur: "Gaussian Blur",
  pixel_blur: "Pixel Blur",
  motion_blur: "Motion Blur",
  bokeh_blur: "Bokeh Blur",
  background_blur: "Background Blur",
  flip: "Flip",
  rotate: "Rotate",
};

/** Plans offered inside the compact paywall (from pricing catalog ids). */
export const PAYWALL_PLAN_OPTIONS = [
  {
    id: "lite",
    name: "Lite",
    audience: "For light use",
    monthly: 10,
    credits: 100,
  },
  {
    id: "pro",
    name: "Pro",
    audience: "For creators",
    monthly: 45,
    credits: 500,
    badge: "Popular",
  },
  {
    id: "max",
    name: "Max",
    audience: "For teams",
    monthly: 650,
    credits: 10000,
  },
];

/**
 * Resolve feature copy, merging optional byTool overrides.
 */
export function getPaywallFeature(featureKey, toolKey = null) {
  const base =
    PAYWALL_FEATURES[featureKey] || PAYWALL_FEATURES.logo_bg_remove;
  if (!toolKey || !base.byTool?.[toolKey]) {
    const { byTool: _byTool, ...rest } = base;
    return rest;
  }
  const { byTool, ...rest } = base;
  return { ...rest, ...byTool[toolKey] };
}

/**
 * Entitlement check for gated features.
 * Wire to real subscription / plan later — currently always blocks so the
 * paywall can be exercised across Watermark Maker / PDF / Word.
 *
 * TODO: Remove sign_pdf_per_page from this set when re-enabling the paywall.
 */
const PAYWALL_DEV_UNLOCKED = new Set(["sign_pdf_per_page"]);

export function canAccessPaywallFeature(featureKey, _user = null) {
  if (PAYWALL_DEV_UNLOCKED.has(featureKey)) return true;
  return false;
}
